/* eslint-disable @typescript-eslint/camelcase */
import { get, isEmpty } from 'lodash'
import { eachDay, format } from 'date-fns'
import { isWeekend } from '../../../containers/Form/PriceConfiguration/Components/PricesType'

export function getPrice(value, type, weekend = undefined) {
  const price = get(value, type, null)
  return price === ''
    ? weekend && get(value, weekend, null) === ''
      ? null
      : get(value, weekend, null)
    : price
}

const mapTypeToPriceList = type => {
  if (type === 'BedBooking') {
    return 'bedbooking'
  }
  if (type && (type.startsWith('BookingDependent') || type.includes('Booking-'))) {
    return 'booking'
  }
  if (type && type.includes('Airbnb')) {
    return 'airbnb'
  }
  return 'bedbooking'
}

const mapTypeToExternalPricingId = type => {
  const parts = type ? type.split('-') : []
  if (parts[0] === 'BookingDependent' && parts.length >= 3) return parts[2]
  return parts[1]
}

export default function(values, roomId, type, objectId, calendarId, config) {
  const isOnlyWeekend = config.weekendPrice
    ? values.periods
        .map(date => eachDay(date.start, date.end).every(d => isWeekend(d)))
        .every(Boolean)
    : false
  const keepRestrictions = values.keepRestrictions === true

  const body: Record<string, unknown> = {
    id_calendar: calendarId,
    id_object: objectId,
    periods: values.periods.map(date => ({
      end: format(date.end, 'YYYY-MM-DD'),
      start: format(date.start, 'YYYY-MM-DD'),
    })),
    prices: {
      extrabed: {
        long: getPrice(values, 'prices.extrabed.long'),
        short: getPrice(values, 'prices.extrabed.short'),
        standard: isOnlyWeekend
          ? getPrice(values, 'prices.extrabed.weekend')
          : getPrice(
              values,
              'prices.extrabed.standard',
              'prices.extrabed.weekend',
            ),
        weekend: getPrice(values, 'prices.extrabed.weekend'),
      },
      kids:
        values.prices && !isEmpty(values.prices.kids)
          ? values.prices.kids
              .map((k, i) =>
                k
                  ? {
                      kid_id: i,
                      long:
                        k.long !== undefined && k.long !== '' ? k.long : null,
                      short:
                        k.short !== undefined && k.short !== ''
                          ? k.short
                          : null,
                      standard: isOnlyWeekend
                        ? k.weekend !== undefined && k.weekend !== ''
                          ? k.weekend
                          : null
                        : k.standard !== undefined && k.standard !== ''
                        ? k.standard
                        : k.weekend !== undefined && k.weekend !== ''
                        ? k.weekend
                        : null,
                      weekend:
                        k.weekend !== undefined && k.weekend !== ''
                          ? k.weekend
                          : null,
                    }
                  : null,
              )
              .filter(Boolean)
          : undefined,
      long: mapPricesToPersonArr(values, 'prices.long'),
      short: mapPricesToPersonArr(values, 'prices.short'),
      standard: isOnlyWeekend
        ? mapPricesToPersonArr(values, 'prices.weekend')
        : mapPricesToPersonArr(values, 'prices.standard', 'prices.weekend'),
      weekend: mapPricesToPersonArr(values, 'prices.weekend'),
    },
    restrictions: keepRestrictions
      ? {}
      : someValue(values.restrictions, [
          'restriction_min_stay_nights',
          'restriction_max_stay_nights',
          'restriction_min_before_arrival_nights',
          'restriction_min_before_arrival_hours',
          'restriction_max_before_arrival_nights',
          'restriction_max_before_arrival_hours',
          'restriction_exact_stay_nights',
          'restriction_min_stay_thru_nights',
          'restriction_max_stay_thru_nights',
          'restriction_max_stay_thru_nights',
          'restriction_close_at_arrival_days',
          'restriction_close_at_departure_days',
        ])
      ? {
          restriction_close_at_arrival: isEmpty(
            values.restrictions.restriction_close_at_arrival_days,
          )
            ? undefined
            : values.restrictions.restriction_close_at_arrival_days.some(
                d => d,
              ),
          restriction_close_at_arrival_days: arrayToWeekDays(
            values.restrictions.restriction_close_at_arrival_days,
          ),
          restriction_close_at_departure: isEmpty(
            values.restrictions.restriction_close_at_departure_days,
          )
            ? undefined
            : values.restrictions.restriction_close_at_departure_days.some(
                d => d,
              ),
          restriction_close_at_departure_days: arrayToWeekDays(
            values.restrictions.restriction_close_at_departure_days,
          ),
          restriction_exact_stay: values.restrictions
            .restriction_exact_stay_nights
            ? values.restrictions.restriction_exact_stay_nights
            : undefined,
          restriction_exact_stay_days: arrayToWeekDays(
            values.restrictions.restriction_exact_stay_days,
          ),
          restriction_max_before_arrival: arrayToHours(
            values.restrictions.restriction_max_before_arrival_nights,
            values.restrictions.restriction_max_before_arrival_hours,
          ),
          restriction_max_before_arrival_days: arrayToWeekDays(
            values.restrictions.restriction_max_before_arrival_days,
          ),
          restriction_max_stay_thru: values.restrictions
            .restriction_max_stay_thru_nights
            ? values.restrictions.restriction_max_stay_thru_nights
            : undefined,
          restriction_max_stay_thru_days: arrayToWeekDays(
            values.restrictions.restriction_max_stay_thru_days,
          ),
          restriction_max_stay: values.restrictions.restriction_max_stay_nights
            ? values.restrictions.restriction_max_stay_nights
            : undefined,
          restriction_max_stay_days: arrayToWeekDays(
            values.restrictions.restriction_max_stay_days,
          ),

          restriction_min_before_arrival: arrayToHours(
            values.restrictions.restriction_min_before_arrival_nights,
            values.restrictions.restriction_min_before_arrival_hours,
          ),
          restriction_min_before_arrival_days: arrayToWeekDays(
            values.restrictions.restriction_min_before_arrival_days,
          ),
          restriction_min_stay: values.restrictions.restriction_min_stay_nights
            ? values.restrictions.restriction_min_stay_nights
            : undefined,
          restriction_min_stay_days: arrayToWeekDays(
            values.restrictions.restriction_min_stay_days,
          ),
          restriction_min_stay_thru: values.restrictions
            .restriction_min_stay_thru_nights
            ? values.restrictions.restriction_min_stay_thru_nights
            : undefined,
          restriction_min_stay_thru_days: arrayToWeekDays(
            values.restrictions.restriction_min_stay_thru_days,
          ),
          // restriction_min_stay: values.details_min_length
          //   ? values.details_min_length[7]
          //   : undefined,
          // restriction_min_stay_days: arrayToWeekDays(values.details_min_length),
          // restriction_min_stay_thru: values.details_min_arrival_length
          //   ? values.details_min_arrival_length[7]
          //   : undefined,
          // restriction_min_stay_thru_days: arrayToWeekDays(
          //   values.details_min_arrival_length,
          // ),
        }
      : {},
    rooms: values.rooms
      ? [roomId, ...values.rooms.map(r => Number(r))]
      : [Number(roomId)],
    pricelist: mapTypeToPriceList(type),
  }
  // Ograniczenia muszą być zapisane tylko w wybranym planie. Dla BedBooking NIE wysyłamy
  // external_pricing_id (backend stosuje do pokoju). Dla planu niezależnego/zależnego
  // wysyłamy id planu, żeby backend zapisał tylko do tego planu (BED-36473).
  const externalId = mapTypeToExternalPricingId(type)
  if (externalId !== undefined && externalId !== null && externalId !== '') {
    body.external_pricing_id = externalId
  }
  if (keepRestrictions) {
    body.ignore_restrictions = 1
  }
  return body
}

function mapPricesToPersonArr(values, type, weekend = undefined) {
  const obj = {}
  const prices = get(values, type)
  const pricesWeekend = weekend ? get(values, weekend) : undefined

  if (isEmpty(prices)) {
    if (isEmpty(pricesWeekend)) {
      return obj
    } else {
      Object.keys(pricesWeekend).forEach(key => {
        obj[key] = pricesWeekend[key] === '' ? null : pricesWeekend[key]
      })
      return obj
    }
  } else {
    Object.keys(prices).forEach(key => {
      obj[key] = prices[key] === '' ? null : prices[key]
    })
    return obj
  }
}

function arrayToWeekDays(arr) {
  if (isEmpty(arr)) {
    return undefined
  }

  const obj = {}

  arr.forEach((a, i) => {
    obj[i] = !!a
  })

  return obj
}

function arrayToHours(days, hours) {
  if (!days && !hours) {
    return undefined
  }
  if (hours) {
    if (days) {
      return Number(days) * 24 + Number(hours)
    }
    return Number(hours)
  }
  if (days && !hours) {
    return Number(days) * 24
  }
}

function someValue(values, arr: string[]) {
  return arr.map(r => get(values, r, false)).some(r => Boolean(r))
}
